import { ChangeDetectionStrategy, ChangeDetectorRef, Component, OnInit } from '@angular/core';
import { Observable } from 'rxjs';
import { tap } from 'rxjs/operators';
import { CompanyService } from 'src/app/core/services/company.service';
import { ForecastService } from 'src/app/core/services/forecast.service';
import { PusherService } from 'src/app/core/services/pusher.service';
import { ProcessingService } from 'src/app/core/services/processing.service';
import { BaseComponent } from 'src/app/core/infrastructure/classes/base-component';
import { HeaderService } from 'src/app/core/services/header.service';
import { Company } from 'src/app/core/models/company';
import { ModalService } from '@stockaid/services';
import { GenericModalComponent } from 'src/app/theme/shared/components/modal/generic-modal/generic-modal.component';
import { NotificationKey } from 'src/app/core/infrastructure/enums/notification.enum';
import { ForecastProgressTracker } from 'src/app/core/infrastructure/classes/forecast-progress-tracker';

@Component({
  selector: 'app-nav-forecast',
  templateUrl: './nav-forecast.component.html',
  styleUrls: ['./nav-forecast.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class NavForecastComponent extends BaseComponent implements OnInit {
  isHide = false;
  lastForecast: Date;
  isProcessing = false;
  isSyncInProcess = false;
  currentCompany: Company;
  header;
  message;
  willRemindToRunForecast: boolean = true;
  willShowRemindingDialog: boolean = this.willRemindToRunForecast;
  private readonly forecastTracker = new ForecastProgressTracker();

  get progress(): number { return this.forecastTracker.progress; }

  constructor(
    private foreCastService: ForecastService,
    private companyService: CompanyService,
    private pusherService: PusherService,
    private processingService: ProcessingService,
    private headerService: HeaderService,
    private modalService: ModalService,
    private cdr: ChangeDetectorRef,
  ) {
    super();
  }

  ngOnInit() {
    this.pusherService.userChannel.bind('notification', (data) => {
      if (
        data?.companyKey !== this.currentCompany?.companyKey ||
        (
          data?.key !== NotificationKey.forecastStatus &&
          data?.key !== NotificationKey.summaryStatus
        )
      ) {
        return;
      }

      this.forecastTracker.handleNotification(data);

      if (data.key === NotificationKey.forecastStatus) {
        switch (data.type) {
          case 'title':
            this.isProcessing = true;
            this.isSyncInProcess = true;
            break;
          case 'header':
            this.header = data.msg;
            break;
          case 'message': {
            const parsed = Number.parseFloat(data.msg);
            if (parsed <= 0) {
              this.message = data.msg;
            }
            break;
          }
        }
      } else if (data.key === NotificationKey.summaryStatus) {
        switch (data.type) {
          case 'title':
            // Non-empty msg means this is a queue/duplicate-job notification, not a Phase 2 init.
            // Ignore it to prevent queue traffic messages from polluting the progress overlay.
            if (data.msg) { break; }
            // Multiple parallel batch workers fire this — only initialise Phase 2 once
            if (this.forecastTracker.phase2StageCount === 0 && !this.isProcessing) {
              this.isProcessing = true;
              this.isSyncInProcess = true;
            }
            break;
          case 'header':
            this.header = data.msg;
            this.message = '';
            break;
          case 'message':
            this.message = data.msg;
            break;
        }
      }

      if (data.type === 'notice' || data.type === 'close') {
        this.isProcessing = false;
        this.isSyncInProcess = false;
        this.message = '';
        this.header = '';
        
        // reload page after forecast successfully
        // make sure reload page only when run forecast
        if (data.key === NotificationKey.summaryStatus && data.type === 'close') {
          window.location.reload();
        }
        
        this.getCurrentCompany(true).subscribe();
      }

      this.cdr.markForCheck();
    });

    this.processingService.processingStartedSync
      .pipe(this.autoCleanUp())
      .subscribe((data) => {
        this.isSyncInProcess = data;
      });

    this.processingService.processingStoppedSync
      .pipe(this.autoCleanUp())
      .subscribe((data) => {
        this.isSyncInProcess = data;
      });

    this.headerService
      .getCompany()
      .pipe(this.autoCleanUp())
      .subscribe((data) => {
        this.isSyncInProcess = !!data?.jobInitiator;
      });

    this.headerService.requestToOpenForecastSubject.pipe(
      this.autoCleanUp()
    ).subscribe(() => {
      this.togglePopup();
    })

    this.headerService.notifyForecastSubject
      .pipe(this.autoCleanUp())
      .subscribe((willRemind) => {
        this.willRemindToRunForecast = this.willShowRemindingDialog = willRemind;
        this.cdr.markForCheck();
      });

    this.getCurrentCompany().subscribe();
  }

  getCurrentCompany(getNew = false): Observable<Company> {
    const getCompanyFunction = getNew
     ? this.companyService.getById(this.currentCompany.companyKey)
     : this.companyService.getCurrentCompany();

    return getCompanyFunction.pipe(
      this.autoCleanUp(),
      tap((company) => {
        this.currentCompany = company;
        this.forecastTracker.setCompanyType(company.companyType);
        this.lastForecast = company.lastForecastDate;
        this.willRemindToRunForecast = this.willShowRemindingDialog = company.willRemindToRunForecast;
      })
    )
  }

  togglePopup() {
    if (this.isSyncInProcess) {
      return;
    }

    this.modalService.open(GenericModalComponent, {
      data: {
        titleIconClass: 'icon feather icon-trending-up',
        title: 'Forecast',
        content: `
          <div class="generic-modal__content text-center mb-3">You have selected to run a Forecast. This will include your latest data in predictive calculations and will incorporate any updated values in your data. Please note that this may take some time.</div>
          <div class="generic-modal__content text-center""><strong>Are you sure that you would like to run a forecast now?</strong></div>
        `,
        successBtn: 'Start Forecast',
        errorBtn: 'Cancel',
        theme: 'light',
      },
      backdrop: 'static',
      onSuccess: () => {
        this.startForecast();
      },
      modalDialogClass: 'sync-modal',
      backdropClass: 'sync-backdrop'
    });
  }

  startForecast() {
    if (this.isSyncInProcess || this.isProcessing) {
      return;
    }

    this.foreCastService.runForecast(this.currentCompany)
      .subscribe(() => {
        this.isProcessing = true;
      });
  }
}
